'use client';

/**
 * Coin Balance Hook with React Query Caching - LOAD ONCE, PERSIST FOREVER
 *
 * Architecture:
 * 1. Initial balance loaded ONCE at ROOT level via loadCoinBalanceSafe()
 * 2. Passed to CoinBalanceProvider in RootProviders
 * 3. This hook uses that initialData for instant display (no loading spinner)
 * 4. Cache NEVER auto-refetches - only updates when explicitly invalidated
 * 5. After spend/purchase, cache is invalidated and refetched via RPC
 *
 * Key settings:
 * - staleTime: Infinity - Data NEVER becomes stale automatically
 * - gcTime: Infinity - Cache NEVER gets garbage collected
 * - refetchOnMount: false - Don't refetch on component mount
 * - refetchOnWindowFocus: false - Don't refetch on tab focus
 *
 * Security: Users cannot modify their balance through cache manipulation.
 * The server always validates before deducting coins.
 */
import { useCallback } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { getWalletBalance } from '~/lib/server/actions/wallet-actions';

import {
  type CoinBalanceData,
  useInitialCoinBalance,
} from './coin-balance-provider';

const QUERY_KEY = ['wallet:balance'];

export type { CoinBalanceData };

/**
 * Hook to get the current user's coin balance with PERMANENT caching.
 *
 * Uses initialData from CoinBalanceProvider (loaded once at root level).
 * Cache persists forever and only updates when invalidated via useInvalidateCoinBalance().
 *
 * @returns Query result with balance data
 */
export function useCoinBalance() {
  const initialBalance = useInitialCoinBalance();

  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<CoinBalanceData> => {
      const wallet = await getWalletBalance();
      return {
        coins: wallet?.coins ?? 0,
        totalEarned: wallet?.totalEarned ?? 0,
        totalSpent: wallet?.totalSpent ?? 0,
      };
    },
    // NEVER auto-refetch - only invalidate manually after spend/purchase
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    // Use server-loaded data as initial (no loading spinner!)
    initialData: initialBalance ?? undefined,
    // NEVER automatically mark as stale - only manual invalidation
    staleTime: Infinity,
    // NEVER garbage collect the cache
    gcTime: Infinity,
  });
}

/**
 * Hook to invalidate the coin balance cache.
 * Call this after any operation that changes the user's balance:
 * - After spending coins (image generation, chat unlock, etc.)
 * - After purchasing coins (on success page redirect)
 *
 * @returns Function to invalidate the balance cache
 */
export function useInvalidateCoinBalance() {
  const queryClient = useQueryClient();

  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
    [queryClient],
  );
}

/**
 * Hook to get just the coin count (convenience wrapper)
 */
export function useCoinCount() {
  const { data, isLoading, error } = useCoinBalance();

  return {
    balance: data?.coins ?? 0,
    isLoading,
    error,
  };
}
